import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Check, Copy, ExternalLink, FileImage, Folder as FolderIcon, Link2, Lock, Music2, Video as VideoIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ProjectShell } from "@/components/projects/project-shell";
import { useProject } from "@/hooks/use-projects";
import {
  useShareDetail,
  useUpdateShare,
  useShareVisits,
  type ShareDetailItem,
} from "@/hooks/use-share";
import { cn } from "@/lib/utils";

type Props = { projectsBasePath: string };

function shareUrl(shortToken: string) {
  return `${window.location.origin}/s/${shortToken}`;
}
function titleOf(name: string | null, createdAt?: string) {
  if (name && name.trim()) return name.trim();
  return createdAt
    ? new Date(createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "Untitled share";
}

export function EditSharePage({ projectsBasePath }: Props) {
  const { projectId, shareId } = useParams<{ projectId: string; shareId: string }>();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "activity" ? "activity" : "settings";

  const { data: project } = useProject(projectId);
  const { data: share, isLoading } = useShareDetail(shareId);
  const update = useUpdateShare();
  const [copied, setCopied] = useState(false);

  const projectPath = `${projectsBasePath}/${projectId}`;
  const prefix = projectsBasePath.startsWith("/admin") ? "/admin" : "/app";
  const watchPath = `${prefix}/watch`;
  const foldersBasePath = `${projectPath}/folders`;

  const title = useMemo(() => titleOf(share?.name ?? null, share?.createdAt), [share]);

  function set<T extends Record<string, unknown>>(body: T) {
    if (shareId) update.mutate({ shareId, body });
  }

  async function copy() {
    if (!share) return;
    await navigator.clipboard.writeText(shareUrl(share.shortToken));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <ProjectShell
      projectId={projectId}
      projectsBasePath={projectsBasePath}
      crumbs={[
        { label: project?.name ?? "…", to: projectPath },
        { label: "All Shares", to: `${projectPath}/shares` },
        { label: title },
      ]}
    >
      {isLoading || !share ? (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Left — asset grid */}
          <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <input
              defaultValue={share.name ?? ""}
              placeholder={title}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (share.name ?? "")) set({ name: v || null });
              }}
              className="mb-5 w-full max-w-lg border-none bg-transparent font-display text-2xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground"
            />
            <div className="mb-3 text-sm text-muted-foreground">
              {share.items.length} {share.items.length === 1 ? "Asset" : "Assets"}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {share.items.map((it) => (
                <ShareAssetCard
                  key={it.id}
                  item={it}
                  to={
                    it.type === "folder"
                      ? `${foldersBasePath}/${it.itemId}`
                      : `${watchPath}/${it.itemId}`
                  }
                />
              ))}
            </div>
          </div>

          {/* Right — settings / activity panel */}
          <aside className="flex w-[360px] shrink-0 flex-col border-l border-border">
            <div className="flex shrink-0 border-b border-border">
              <TabButton active={tab === "settings"} onClick={() => setParams({}, { replace: true })}>Settings</TabButton>
              <TabButton active={tab === "activity"} onClick={() => setParams({ tab: "activity" }, { replace: true })}>Activity</TabButton>
            </div>

            <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto p-4">
              {tab === "settings" ? (
                <SettingsPanel share={share} onSet={set} pending={update.isPending} />
              ) : (
                <ActivityPanel shareId={shareId} />
              )}
            </div>

            <div className="shrink-0 space-y-2 border-t border-border p-4">
              <Button variant="outline" className="w-full" onClick={() => window.open(`${shareUrl(share.shortToken)}?preview=1`, "_blank", "noopener")}>
                <ExternalLink className="h-4 w-4" /> Open Share Link
              </Button>
              <Button className="w-full" onClick={copy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy Link"}
              </Button>
            </div>
          </aside>
        </div>
      )}
    </ProjectShell>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex-1 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function SettingsPanel({
  share,
  onSet,
  pending,
}: {
  share: NonNullable<ReturnType<typeof useShareDetail>["data"]>;
  onSet: (body: Record<string, unknown>) => void;
  pending: boolean;
}) {
  return (
    <div className="space-y-6 text-sm">
      <Row label="Link visibility" desc="Turn off to disable the link without deleting it.">
        <Switch checked={share.visibilityEnabled} onCheckedChange={(v) => onSet({ visibilityEnabled: v })} />
      </Row>

      <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-2 py-1.5 text-xs text-muted-foreground">
        <Link2 className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">/s/{share.shortToken}</span>
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Permissions</div>
        <div className="space-y-3">
          <Row label="Comments"><Switch checked={share.commentAllowed} onCheckedChange={(v) => onSet({ commentAllowed: v })} /></Row>
          <Row label="Downloads"><Switch checked={share.downloadAllowed} onCheckedChange={(v) => onSet({ downloadAllowed: v })} /></Row>
          <Row label="Show all versions"><Switch checked={share.showAllVersions} onCheckedChange={(v) => onSet({ showAllVersions: v })} /></Row>
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Expiry</div>
        <Input
          type="date"
          defaultValue={share.expiresAt ? new Date(share.expiresAt).toISOString().slice(0, 10) : ""}
          onChange={(e) => {
            if (!e.target.value) return;
            const d = new Date(e.target.value + "T23:59:59");
            if (!Number.isNaN(d.getTime()) && d.getTime() > Date.now()) onSet({ expiresAt: d.toISOString() });
          }}
          className="h-9"
        />
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Passphrase</div>
        <PassphraseControl
          hasPassphrase={share.hasPassphrase}
          hint={share.passphraseHint ?? null}
          pending={pending}
          onSet={onSet}
        />
      </div>
    </div>
  );
}

// Passphrase set/clear control. The passphrase itself is never returned by the
// API (only `hasPassphrase` + `hint`), so setting always means typing a fresh
// one. Empty-string / null clears both the passphrase and the hint server-side.
function PassphraseControl({
  hasPassphrase,
  hint,
  pending,
  onSet,
}: {
  hasPassphrase: boolean;
  hint: string | null;
  pending: boolean;
  onSet: (body: Record<string, unknown>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [pass, setPass] = useState("");
  const [hintDraft, setHintDraft] = useState(hint ?? "");
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setPass("");
    setHintDraft(hint ?? "");
    setError(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setPass("");
    setError(null);
  };
  const save = () => {
    if (pass.length < 4 || pass.length > 128) {
      setError("Passphrase must be 4–128 characters.");
      return;
    }
    onSet({ passphrase: pass, passphraseHint: hintDraft.trim() || null });
    setEditing(false);
    setPass("");
    setError(null);
  };
  const remove = () => {
    onSet({ passphrase: null });
    setEditing(false);
    setPass("");
  };

  if (hasPassphrase && !editing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-2.5 py-2 text-xs">
          <Lock className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-foreground">Passphrase required</span>
        </div>
        {hint && <div className="text-xs text-muted-foreground">Hint: {hint}</div>}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8" disabled={pending} onClick={startEdit}>
            Change
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-destructive hover:text-destructive" disabled={pending} onClick={remove}>
            Remove
          </Button>
        </div>
      </div>
    );
  }

  if (!hasPassphrase && !editing) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">Guests must enter a passphrase before they can open this share.</p>
        <Button variant="outline" size="sm" className="h-8" disabled={pending} onClick={startEdit}>
          <Lock className="h-3.5 w-3.5" /> Set passphrase
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Input
        type="password"
        autoFocus
        value={pass}
        placeholder="New passphrase"
        autoComplete="new-password"
        onChange={(e) => setPass(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); save(); }
          if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        className="h-9"
      />
      <Input
        type="text"
        value={hintDraft}
        placeholder="Hint (optional)"
        onChange={(e) => setHintDraft(e.target.value)}
        className="h-9"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" className="h-8" disabled={pending} onClick={save}>Save</Button>
        <Button variant="ghost" size="sm" className="h-8" disabled={pending} onClick={cancel}>Cancel</Button>
      </div>
    </div>
  );
}

function ActivityPanel({ shareId }: { shareId: string | undefined }) {
  const { data, isLoading } = useShareVisits(shareId);
  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const visits = data?.visits ?? [];
  if (visits.length === 0) {
    return <div className="text-sm text-muted-foreground">No one has opened this link yet.</div>;
  }
  return (
    <ul className="space-y-3">
      {visits.map((v) => (
        <li key={v.visitorId} className="flex items-start gap-2">
          <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-secondary text-[10px] font-semibold uppercase text-muted-foreground">
            {v.name.slice(0, 2)}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm text-foreground">{v.name}</div>
            <div className="truncate text-xs text-muted-foreground">{v.email}</div>
            <div className="text-[11px] text-muted-foreground">Opened {new Date(v.lastSeen).toLocaleString()}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Row({ label, desc, children }: { label: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-foreground">{label}</div>
        {desc && <div className="text-xs text-muted-foreground">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

function ShareAssetCard({ item, to }: { item: ShareDetailItem; to: string }) {
  const Icon = item.type === "folder" ? FolderIcon : item.assetType === "audio" ? Music2 : item.assetType === "image" ? FileImage : VideoIcon;
  return (
    <Link
      to={to}
      className="group flex flex-col gap-2 rounded-xl border border-border bg-card p-3 transition-colors hover:border-primary/60 hover:bg-secondary/40"
    >
      <div className="grid aspect-video place-items-center overflow-hidden rounded-lg bg-secondary/60 text-muted-foreground">
        {item.type === "video" && item.assetType === "video" && item.cdnUrl ? (
          <video src={item.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
        ) : item.type === "video" && item.assetType === "image" && item.cdnUrl ? (
          <img src={item.cdnUrl} alt={item.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <Icon className="h-7 w-7" strokeWidth={1.5} />
        )}
      </div>
      <div className="truncate text-sm font-medium text-foreground">{item.name}</div>
    </Link>
  );
}
